"use client";

import Link from "next/link";
import {
  BoxSelect,
  Captions,
  Cloud as LucideCloud,
  Cpu as LucideCpu,
  Crosshair,
  MessageCircleQuestion,
  Microscope,
  Shapes,
  Sparkles,
} from "lucide-react";
import {
  useEffect,
  useState,
  type ComponentType,
  type ReactNode,
} from "react";
import {
  MoondreamWordmark,
  IconSun,
  IconMoon,
  IconGithub,
  IconArrowRight,
  IconArrowUpRight,
  IconCopy,
  IconMenu,
  IconX,
  IconCheck,
  IconBolt,
  IconSliders,
  IconTarget,
  IconCpu,
  IconGlobe,
  Code,
  type IconProps,
} from "./icons";
import { InferenceDemo } from "./inference-demo";
import { BenchmarkChart, HardwareRange, PartnerClouds } from "./charts";
import { ContactFormDialog } from "@/components/contact-form-dialog";
import { UserOrNot } from "@/components/layout/user-or-not";
import { NavUser } from "@/components/layout/nav-user";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

// ---- Nav ----
// Mirrors the "Products" dropdown content used by the production header at "/"
// (see components/layout/header-content.tsx). Item titles, descriptions, and
// icons match.
type ProductLink = {
  title: string;
  description: string;
  href: string;
  icon: ReactNode;
  items?: Array<{
    title: string;
    href: string;
    icon: ReactNode;
  }>;
};

const SKILL_LINKS = [
  {
    title: "Detect",
    href: "/skills/detect",
    icon: <BoxSelect size={14} strokeWidth={1.75} />,
  },
  {
    title: "Caption",
    href: "/skills/caption",
    icon: <Captions size={14} strokeWidth={1.75} />,
  },
  {
    title: "Point",
    href: "/skills/point",
    icon: <Crosshair size={14} strokeWidth={1.75} />,
  },
  {
    title: "Segment",
    href: "/skills/segment",
    icon: <Shapes size={14} strokeWidth={1.75} />,
  },
  {
    title: "Query",
    href: "/skills/query",
    icon: <MessageCircleQuestion size={14} strokeWidth={1.75} />,
  },
];

const PRODUCT_LINKS: ProductLink[] = [
  {
    title: "Models",
    description: "State-of-the-art vision language models",
    href: "/p/models",
    icon: <Sparkles size={20} strokeWidth={1.75} />,
    items: SKILL_LINKS,
  },
  {
    title: "Lens",
    description: "Fine-tune Moondream for your use case",
    href: "/p/lens",
    icon: <Microscope size={20} strokeWidth={1.75} />,
  },
  {
    title: "Photon",
    description: "High-performance local inference engine",
    href: "/p/photon",
    icon: <LucideCpu size={20} strokeWidth={1.75} />,
  },
  {
    title: "Cloud",
    description: "Managed inference in the cloud",
    href: "/p/cloud",
    icon: <LucideCloud size={20} strokeWidth={1.75} />,
  },
];

const MOBILE_NAV_LINKS = [
  { title: "Console", href: "/org/me/dashboard" },
  { title: "Docs", href: "https://docs.moondream.ai" },
  { title: "Playground", href: "/playground" },
  { title: "Pricing", href: "/pricing" },
  { title: "Blog", href: "/blog" },
] as const;

const ProductsMenu = () => {
  return (
    <NavigationMenuItem>
      <NavigationMenuTrigger className="nav-link nav-menu-trigger">
        Products
      </NavigationMenuTrigger>
      <NavigationMenuContent>
        <div className="nav-products-menu">
          {PRODUCT_LINKS.map((item) => (
            <div className="nav-products-group" key={item.href}>
              <NavigationMenuLink asChild>
                <Link href={item.href} className="nav-products-item">
                  <span className="nav-products-item-icon">{item.icon}</span>
                  <span className="nav-products-item-text">
                    <span className="nav-products-item-title">{item.title}</span>
                    <span className="nav-products-item-desc">
                      {item.description}
                    </span>
                  </span>
                </Link>
              </NavigationMenuLink>
              {item.items && (
                <div className="nav-products-subitems">
                  {item.items.map((child) => (
                    <NavigationMenuLink asChild key={child.href}>
                      <Link href={child.href} className="nav-products-subitem">
                        <span className="nav-products-subitem-icon">
                          {child.icon}
                        </span>
                        {child.title}
                      </Link>
                    </NavigationMenuLink>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </NavigationMenuContent>
    </NavigationMenuItem>
  );
};

const HeaderMenuLink = ({
  href,
  children,
}: {
  href: string;
  children: ReactNode;
}) => (
  <NavigationMenuItem>
    <NavigationMenuLink asChild>
      {href.startsWith("/") ? (
        <Link href={href} className="nav-link">
          {children}
        </Link>
      ) : (
        <a href={href} className="nav-link">
          {children}
        </a>
      )}
    </NavigationMenuLink>
  </NavigationMenuItem>
);

const MobileNavLink = ({
  href,
  children,
  className,
  onClick,
}: {
  href: string;
  children: ReactNode;
  className?: string;
  onClick: () => void;
}) =>
  href.startsWith("/") ? (
    <Link href={href} className={className} onClick={onClick}>
      {children}
    </Link>
  ) : (
    <a href={href} className={className} onClick={onClick}>
      {children}
    </a>
  );

const NavLogin = () => (
  <UserOrNot
    userContent={(user) => <NavUser user={user} />}
    noUserContent={
      <Link
        prefetch
        href={{ pathname: "/login", query: { redirectTo: "/org/me/api-keys" } }}
        className="nav-link"
      >
        Sign up/in
      </Link>
    }
  />
);

export const Nav = ({
  theme,
  onToggleTheme,
}: {
  theme: "dark" | "light";
  onToggleTheme: () => void;
}) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 8);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  return (
    <header className={`nav ${scrolled ? "nav-scrolled" : ""}`}>
      <div className="container nav-row">
        <div className="nav-left">
          <Link
            href="/"
            aria-label="Moondream home"
            style={{ color: "inherit", textDecoration: "none" }}
          >
            <MoondreamWordmark />
          </Link>
          <nav aria-label="Main navigation">
            <NavigationMenu className="nav-links">
              <NavigationMenuList className="nav-menu-list">
                <ProductsMenu />
                <HeaderMenuLink href="https://docs.moondream.ai">
                  Docs
                </HeaderMenuLink>
                <HeaderMenuLink href="/playground">Playground</HeaderMenuLink>
                <HeaderMenuLink href="/pricing">Pricing</HeaderMenuLink>
                <HeaderMenuLink href="/blog">Blog</HeaderMenuLink>
              </NavigationMenuList>
            </NavigationMenu>
          </nav>
        </div>
        <div className="nav-right">
          <button
            type="button"
            className="icon-btn"
            onClick={onToggleTheme}
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <IconSun size={18} /> : <IconMoon size={18} />}
          </button>
          <a
            href="https://github.com/m87-labs/moondream"
            className="gh-chip"
            aria-label="GitHub repository · 9.9k stars"
            target="_blank"
            rel="noopener noreferrer"
          >
            <IconGithub size={16} />
            <span className="gh-chip-star" aria-hidden="true">
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M12 2l2.9 6.9L22 10l-5.5 4.8L18 22l-6-3.5L6 22l1.5-7.2L2 10l7.1-1.1z" />
              </svg>
            </span>
            <span className="gh-chip-count tabular mono">9.9k</span>
          </a>
          <Link href="/playground" className="btn btn-primary btn-sm">
            Try the model <IconArrowRight size={14} />
          </Link>
          <NavLogin />
          <button
            type="button"
            className="icon-btn mobile-only"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label="Menu"
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <IconX size={18} /> : <IconMenu size={18} />}
          </button>
        </div>
      </div>
      {mobileOpen ? (
        <div className="mobile-nav-panel">
          <div className="container mobile-nav-inner">
            <div className="mobile-nav-products">
              {PRODUCT_LINKS.map((item) => (
                <MobileNavLink
                  key={item.href}
                  href={item.href}
                  className="mobile-nav-product"
                  onClick={() => setMobileOpen(false)}
                >
                  <span className="mobile-nav-product-icon">{item.icon}</span>
                  <span>{item.title}</span>
                </MobileNavLink>
              ))}
            </div>
            <div className="mobile-nav-links">
              {MOBILE_NAV_LINKS.map((item) => (
                <MobileNavLink
                  key={item.href}
                  href={item.href}
                  className="mobile-nav-link"
                  onClick={() => setMobileOpen(false)}
                >
                  {item.title}
                </MobileNavLink>
              ))}
            </div>
            <div className="mobile-nav-actions">
              <button
                type="button"
                className="mobile-nav-link mobile-nav-action"
                onClick={() => {
                  onToggleTheme();
                  setMobileOpen(false);
                }}
              >
                {theme === "dark" ? <IconSun size={16} /> : <IconMoon size={16} />}
                <span>{theme === "dark" ? "Light mode" : "Dark mode"}</span>
              </button>
              <a
                href="https://github.com/m87-labs/moondream"
                className="mobile-nav-link mobile-nav-action"
                aria-label="GitHub repository"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileOpen(false)}
              >
                <IconGithub size={16} />
                <span>GitHub</span>
              </a>
            </div>
          </div>
        </div>
      ) : null}
    </header>
  );
};

// ---- Hero ----
const HERO = {
  sub: "Build, fine-tune, and deploy visual AI that works on your real-world edge cases.",
};

export const Hero = () => {
  return (
    <section className="hero section">
      <div className="container hero-topline">
        <Link href="/blog/photon-2-launch" className="hero-announce">
          <span className="hero-announce-tag">New</span>
          <span className="hero-announce-text">
            Photon 2.0: Inference engine for Physical AI
          </span>
          <span className="hero-announce-arrow">
            <IconArrowRight size={14} />
          </span>
        </Link>
      </div>
      <div className="container hero-grid">
        <div className="hero-col-text">
          <h1 className="hero-headline t-display-xl">
            State-of-the-art vision for{" "}
            <span className="hl-detect">
              your task.
              <span className="hl-detect-label mono" aria-hidden="true">
                detect · 0.98
              </span>
            </span>
          </h1>
          <p
            className="hero-sub t-body-lg"
            style={{
              color: "var(--text-secondary)",
              maxWidth: 560,
              marginTop: 24,
            }}
          >
            {HERO.sub}
          </p>

          <div className="hero-ctas" style={{ marginTop: 36 }}>
            <Link href="/playground" className="btn btn-primary">
              Try the playground <IconArrowUpRight size={15} />
            </Link>
            <a href="https://docs.moondream.ai" className="btn btn-secondary">
              View docs
            </a>
          </div>

          <InstallSnippet style={{ marginTop: 20 }} />

          <p className="hero-proof mono">
            5M+ monthly downloads · open weights · commercial use
          </p>
        </div>

        <div className="hero-col-demo">
          <InferenceDemo />
        </div>
      </div>
    </section>
  );
};

// Reusable install snippet (originally in Hero, now used in Step 1).
const InstallSnippet = ({ style }: { style?: React.CSSProperties }) => {
  const onCopy = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText("pip install moondream");
    }
    const el = e.currentTarget;
    el.dataset.copied = "1";
    setTimeout(() => {
      delete el.dataset.copied;
    }, 1200);
  };
  return (
    <div className="hero-install" style={style}>
      <span className="hero-install-prompt mono">$</span>
      <span className="hero-install-cmd mono">pip install moondream</span>
      <button
        type="button"
        className="hero-install-copy"
        aria-label="Copy install command"
        onClick={onCopy}
      >
        <IconCopy size={14} />
      </button>
    </div>
  );
};

const OPEN_MODEL_CARDS = [
  {
    href: "/models/moondream_3-1_9B_A2B",
    name: "Moondream 3.1",
    tag: "Recommended",
    featured: true,
    description:
      "Sparse mixture-of-experts architecture with frontier-level visual reasoning, segmentation, and long-context queries.",
    spec: "9B MoE · 2B active",
    skills: ["Query", "Caption", "Detect", "Point", "Segment"],
  },
  {
    href: "/p/models#moondream-2",
    name: "Moondream 2",
    tag: "Stable",
    featured: false,
    description:
      "The production workhorse: compact, proven, commercially friendly, and easy to deploy across GPUs, CPUs, and edge devices.",
    spec: "2B dense",
    skills: ["Query", "Caption", "Detect", "Point"],
  },
  {
    href: "/p/models#moondream-2-0-5b",
    name: "Moondream 2 0.5B",
    tag: "Tiny",
    featured: false,
    description:
      "A small fine-tuning base for constrained hardware where every megabyte matters.",
    spec: "0.5B dense",
    skills: ["Query", "Caption", "Detect", "Point"],
  },
] as const;

const OPEN_MODEL_SKILLS = [
  "Query",
  "Caption",
  "Detect",
  "Point",
  "Segment",
] as const;

// ---- Problem ----
export const ProblemSection = () => (
  <section
    className="section"
    id="problem"
    style={{ paddingTop: 48, paddingBottom: 80 }}
  >
    <div className="container">
      <div className="problem-grid">
        <div>
          <div
            className="t-eyebrow"
            style={{ color: "var(--text-tertiary)" }}
          >
            Production-ready
          </div>
          <h2
            className="t-heading-xl"
            style={{ marginTop: 12, maxWidth: 680 }}
          >
            VLMs are powerful in the lab. Most break on the way to production.
          </h2>
        </div>
        <div className="problem-body">
          <p
            className="t-body-lg"
            style={{ color: "var(--text-secondary)" }}
          >
            Accuracy drops in the field. False positives pile up. Costs spike
            when you run across a fleet of cameras. Latency is off by an order
            of magnitude. Teams give up and conclude VLMs are not ready.
          </p>
          <p
            className="t-body-lg"
            style={{ color: "var(--text-secondary)", marginTop: 16 }}
          >
            Moondream is built for that last mile. An open model to validate
            fit, a fine-tuning platform for your data, an inference engine
            tuned for real-time, and a single vendor to call when it breaks.
          </p>
        </div>
      </div>
    </div>
  </section>
);

// ---- Step header ----
const StepHeader = ({
  num,
  kicker,
  title,
  tagline,
  children,
}: {
  num: string;
  kicker: string;
  title: ReactNode;
  tagline?: string;
  children?: ReactNode;
}) => (
  <div className="step-head">
    <div className="step-num mono">{num}</div>
    <div>
      <div className="t-eyebrow" style={{ color: "var(--text-tertiary)" }}>
        {kicker}
      </div>
      <h2 className="t-heading-xl" style={{ marginTop: 10, maxWidth: 720 }}>
        {title}
      </h2>
      {tagline && (
        <p
          className="t-body-lg"
          style={{
            color: "var(--text-secondary)",
            marginTop: 14,
            maxWidth: 640,
          }}
        >
          {tagline}
        </p>
      )}
      {children}
    </div>
  </div>
);

// ---- Step 1 ----
export const Step1 = () => (
  <section className="section" id="step-1">
    <div className="container">
      <div className="step-head try-intro">
        <div className="step-num mono">01</div>
        <div className="try-intro-grid">
          <div className="try-intro-heading">
            <div className="t-eyebrow" style={{ color: "var(--text-tertiary)" }}>
              Step 1 · Try it
            </div>
            <h2
              className="t-heading-xl"
              style={{ marginTop: 10, maxWidth: 720 }}
            >
              Try the open models. You might already be done.
            </h2>
          </div>
          <div>
            <p
              className="t-body-lg"
              style={{
                color: "var(--text-secondary)",
                marginTop: 0,
                maxWidth: 640,
              }}
            >
              Moondream might already nail your use case out of the box. The
              open models are commercially friendly and can run anywhere. Use
              our playground to try it out or download it and run it yourself.
            </p>
            <div className="step-actions">
              <Link href="/playground" className="btn btn-primary">
                Open playground <IconArrowRight size={14} />
              </Link>
            </div>
            <p
              className="t-body-sm"
              style={{
                color: "var(--text-tertiary)",
                marginTop: 12,
              }}
            >
              No credit card required. $5 in credits added monthly.
            </p>
          </div>
          <div className="try-intro-code">
            <Code
              lang="python"
              tokens={[
                ["c", "# Caption an image in four lines\n"],
                ["k", "import"],
                " moondream ",
                ["k", "as"],
                " md\n",
                ["k", "from"],
                " PIL ",
                ["k", "import"],
                " Image\n\n",
                "model = md.",
                ["f", "vl"],
                "(api_key=",
                ["s", '"YOUR_API_KEY"'],
                ")\n",
                "image = Image.",
                ["f", "open"],
                "(",
                ["s", '"shelf.jpg"'],
                ")\n",
                "print(model.",
                ["f", "caption"],
                "(image).caption)\n",
                ["c", "# → 'A warehouse shelf with six cardboard cartons…'"],
              ]}
            />
          </div>
        </div>
      </div>
      <div className="model-card-grid">
        {OPEN_MODEL_CARDS.map((model) => (
          <Link
            key={model.name}
            href={model.href}
            className={`model-card ${model.featured ? "model-card-featured" : ""}`}
          >
            <div className="model-card-head">
              <h3>{model.name}</h3>
              <span className={model.featured ? "is-featured" : ""}>
                {model.tag}
              </span>
            </div>
            <p>{model.description}</p>
            <div className="model-card-spec mono">{model.spec}</div>
            <div
              className="model-card-skills"
              aria-label={`${model.name} supported skills`}
            >
              {OPEN_MODEL_SKILLS.map((skill) => {
                const isActive = (model.skills as readonly string[]).includes(
                  skill,
                );
                return (
                  <span
                    key={skill}
                    className={isActive ? "is-active" : "is-inactive"}
                  >
                    {skill}
                  </span>
                );
              })}
            </div>
            <div className="model-card-cta">
              Model card <IconArrowRight size={13} />
            </div>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

// ---- Step 2 ----
export const Step2 = () => (
  <section className="section" id="step-2">
    <div className="container">
      <StepHeader
        num="02"
        kicker="Step 2 · Fine-tune it"
        title="Need more? Lens gets you to production-grade accuracy."
        tagline="Your data is specific, so the model has to be. Lens is a fine-tuning platform with a simple API. No dataset uploads, no infrastructure, no ML team required."
      >
        <div className="lens-cta" style={{ marginTop: 24 }}>
          <Link href="/p/lens" className="btn btn-primary">
            Learn more about Lens <IconArrowRight size={14} />
          </Link>
          <a
            href="https://docs.moondream.ai/finetuning/"
            target="_blank"
            rel="noreferrer"
            className="lens-cta-link"
          >
            Start fine-tuning <IconArrowUpRight size={12} />
          </a>
        </div>
      </StepHeader>

      <div className="lens-grid">
        <div className="lens-rail">
          <LensFeat
            Icon={IconSliders}
            title="Self-serve API"
            body="A simple hosted API — no hardware to rent or manage. Supports SFT and RL. Vibe-code your fine-tune script in minutes."
          />
          <LensFeat
            Icon={IconBolt}
            title="Tune and go"
            body="Your fine-tuned model is instantly ready to run on Moondream Cloud or locally with Photon. No cumbersome download or install step."
          />
          <LensFeat
            Icon={IconTarget}
            title="White-glove option"
            body="Our team handles the labeling protocol, loss design, and evaluation. You keep the weights, the training code, and the data. Unlike ML consulting, you walk away self-sufficient."
          />
          <LensFeat
            Icon={IconCheck}
            title="No massive dataset required"
            body="Our reinforcement-learning fine-tune API can dramatically improve accuracy with as few as 20 labeled images — not thousands."
          />
        </div>
      </div>
    </div>
  </section>
);

const LensFeat = ({
  Icon,
  title,
  body,
}: {
  Icon: (p: IconProps) => ReactNode;
  title: string;
  body: string;
}) => (
  <div className="lens-feat">
    <div className="lens-icon">
      <Icon size={20} />
    </div>
    <div>
      <div className="t-heading-sm">{title}</div>
      <p
        className="t-body-md"
        style={{ color: "var(--text-secondary)", marginTop: 4 }}
      >
        {body}
      </p>
    </div>
  </div>
);

// ---- Step 3 ----
export const Step3 = () => (
  <section className="section step3" id="step-3">
    <div className="container">
      <StepHeader
        num="03"
        kicker="Step 3 · Run it anywhere"
        title="Fast, efficient, and runs anywhere you need it."
        tagline="Once your model is accurate, performance and cost become the next wall. Photon is the inference engine we built to run Moondream in production. Moondream Cloud and partner clouds give you a hosted path if you want one."
      />

      <div className="concerns">
        <Concern
          Icon={IconBolt}
          title="Speed"
          body="Under 500 ms is the difference between a useful answer and a late one. Photon answers a direct query in under 60 ms on an H100, and stays real-time all the way down to Jetson."
          metric="59 ms"
          unit="P50 · H100 · batch 1"
        />
        <Concern
          Icon={IconCpu}
          title="Cost"
          body="A VLM running across a fleet of cameras at the wrong efficiency costs thousands a day. Moondream is the lowest-cost VLM we have measured across the inference providers we tested."
          metric="$0.06"
          unit="per 1K images, cloud"
        />
        <Concern
          Icon={IconGlobe}
          title="Flexibility"
          body="Your deployment story will change. Start in the cloud, move to the edge, or run air-gapped. You pick the hardware. The model and APIs stay the same."
          metric="9"
          unit="benchmarked hardware tiers"
        />
      </div>

      <div className="bench-wrap">
        <BenchmarkChart />
      </div>

      <div className="hw-wrap">
        <HardwareRange />
      </div>

      <div className="partners-wrap">
        <div
          className="t-eyebrow"
          style={{ color: "var(--text-tertiary)", marginBottom: 12 }}
        >
          Available on
        </div>
        <PartnerClouds />
      </div>

      <div className="photon-code-wrap">
        <div className="photon-code-head">
          <div>
            <div
              className="t-eyebrow"
              style={{ color: "var(--text-tertiary)" }}
            >
              Photon
            </div>
            <div className="t-heading-md" style={{ marginTop: 4 }}>
              Same code. Edge, workstation, server.
            </div>
          </div>
          <a
            href="https://docs.moondream.ai/running-locally"
            target="_blank"
            rel="noreferrer"
            className="btn btn-ghost btn-sm"
          >
            Read the Photon docs <IconArrowRight size={12} />
          </a>
        </div>
        <Code
          lang="python"
          tokens={[
            ["k", "import"],
            " moondream ",
            ["k", "as"],
            " md\n",
            ["k", "from"],
            " PIL ",
            ["k", "import"],
            " Image\n\n",
            ["c", "# Initialize with local GPU inference\n"],
            "model = md.",
            ["f", "vl"],
            "(api_key=",
            ["s", '"YOUR_API_KEY"'],
            ", local=",
            ["k", "True"],
            ")\n\n",
            ["c", "# Load an image\n"],
            "image = Image.",
            ["f", "open"],
            "(",
            ["s", '"path/to/image.jpg"'],
            ")\n\n",
            ["c", "# Generate a caption\n"],
            "caption = model.",
            ["f", "caption"],
            "(image)[",
            ["s", '"caption"'],
            "]\n",
            ["f", "print"],
            "(",
            ["s", '"Caption:"'],
            ", caption)\n",
          ]}
        />
      </div>
    </div>
  </section>
);

const Concern = ({
  Icon,
  title,
  body,
  metric,
  unit,
}: {
  Icon: (p: IconProps) => ReactNode;
  title: string;
  body: string;
  metric: string;
  unit: string;
}) => (
  <div className="concern">
    <div className="concern-icon">
      <Icon size={22} />
    </div>
    <div className="t-heading-md">{title}</div>
    <p
      className="t-body-md"
      style={{ color: "var(--text-secondary)", marginTop: 6 }}
    >
      {body}
    </p>
    <div className="concern-metric tabular mono">
      <span className="concern-num">{metric}</span>
      <span className="concern-unit">{unit}</span>
    </div>
  </div>
);

// ---- Step 4 ----
export const Step4 = () => (
  <section className="section" id="step-4">
    <div className="container">
      <StepHeader
        num="04"
        kicker="Step 4 · Keep it running"
        title="Launch is just the start."
        tagline="One vendor for the full stack. Models drift. Engineers leave. New use cases appear. With stitched-together vendors, nobody owns the outage. On Moondream, we do."
      />

      <div className="support-main card">
        <div className="support-col">
          <div
            className="t-eyebrow"
            style={{ color: "var(--text-tertiary)" }}
          >
            Competitor stack
          </div>
          <ul className="support-list stitched">
            <li>
              <IconX size={16} /> Model vendor (weights only)
            </li>
            <li>
              <IconX size={16} /> Fine-tuning vendor (your data goes
              elsewhere)
            </li>
            <li>
              <IconX size={16} /> Inference provider (different SLA)
            </li>
            <li>
              <IconX size={16} /> Your on-call engineer (owns everything)
            </li>
          </ul>
        </div>
        <div className="support-divider" />
        <div className="support-col">
          <div className="t-eyebrow" style={{ color: "var(--accent-text)" }}>
            Moondream
          </div>
          <ul className="support-list whole">
            <li>
              <IconCheck size={16} /> Model, weights, and roadmap
            </li>
            <li>
              <IconCheck size={16} /> Lens fine-tuning and evals
            </li>
            <li>
              <IconCheck size={16} /> Photon and Moondream Cloud
            </li>
            <li>
              <IconCheck size={16} /> One team on call, 24/7 on enterprise
              plans
            </li>
          </ul>
          <Link
            href="/pricing"
            className="btn btn-secondary"
            style={{ marginTop: 24 }}
          >
            See Plans <IconArrowRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  </section>
);

// ---- Products ----
type Product = {
  // Accepts both our custom IconProps components and lucide-react icons —
  // both render with a `size` prop so that's all this section needs.
  Icon: ComponentType<{ size?: number }>;
  name: string;
  desc: string;
  cta: string;
  href: string;
};

// Product icons mirror the header dropdown (see PRODUCT_LINKS above).
// Support is not a header item, so it keeps its own icon.
const PRODUCTS: Product[] = [
  {
    Icon: Sparkles,
    name: "Open Models",
    desc: "The foundation. Free for commercial use. 2B, 1B, and 0.5B checkpoints on Hugging Face.",
    cta: "View on Hugging Face",
    href: "/p/models",
  },
  {
    Icon: Microscope,
    name: "Lens",
    desc: "Fine-tuning with a simple API. Self-serve or white-glove. You keep the weights.",
    cta: "Start fine-tuning",
    href: "/p/lens",
  },
  {
    Icon: LucideCpu,
    name: "Photon",
    desc: "Inference engine. Hand-tuned kernels. Mac, Windows, CUDA — Jetson to B200.",
    cta: "Run Moondream fast",
    href: "/p/photon",
  },
  {
    Icon: LucideCloud,
    name: "Moondream Cloud",
    desc: "Hosted inference. OpenAI-compatible API. Pay per image, no commitment.",
    cta: "Get an API key",
    href: "/p/cloud",
  },
];

export const ProductsSection = () => (
  <section className="section" id="products">
    <div className="container">
      <div className="t-eyebrow" style={{ color: "var(--text-tertiary)" }}>
        The platform
      </div>
      <h2 className="t-heading-xl" style={{ marginTop: 10, maxWidth: 720 }}>
        Four products that work together. Use one, or all four.
      </h2>

      <div className="products-grid">
        {PRODUCTS.map((p, i) => {
          const Icon = p.Icon;
          return (
            <Link href={p.href} className="product-card" key={p.name}>
              <div className="product-head">
                <div className="product-icon">
                  <Icon size={20} />
                </div>
                <span className="mono product-num">0{i + 1}</span>
              </div>
              <div className="t-heading-md" style={{ marginTop: 24 }}>
                {p.name}
              </div>
              <p
                className="t-body-md"
                style={{ color: "var(--text-secondary)", marginTop: 8 }}
              >
                {p.desc}
              </p>
              <div className="product-cta">
                {p.cta} <IconArrowRight size={14} />
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  </section>
);

// ---- Use cases ----
const USE_CASES = [
  { k: "Computer use agents", d: "Understand UI, drive computers, automate workflows." },
  { k: "Manufacturing QA", d: "Defect tracking on the line, frame by frame." },
  { k: "Public safety", d: "Monitor compliance and incidents across camera fleets." },
  { k: "Retail analytics", d: "Shelf state, price-tag audits, staff coverage." },
  { k: "Sports tracking", d: "Player positioning and broadcast automation in real time." },
  { k: "Property tours", d: "Index and caption thousands of listings overnight." },
  { k: "Logistics", d: "Carton, pallet, and placard reading in the yard." },
  { k: "Compliance monitoring", d: "PPE, zone, and policy checks across facilities." },
];

export const UseCasesSection = () => (
  <section className="section" id="use-cases" style={{ paddingTop: 96 }}>
    <div className="container">
      <div className="t-eyebrow" style={{ color: "var(--text-tertiary)" }}>
        In production at
      </div>
      <h2 className="t-heading-xl" style={{ marginTop: 10, maxWidth: 720 }}>
        The kinds of problems Moondream is already solving.
      </h2>
      <p
        className="t-body-lg"
        style={{
          color: "var(--text-secondary)",
          marginTop: 14,
          maxWidth: 620,
        }}
      >
        A sample of the use cases our customers ship. Each of these runs the
        same open model, often with a Lens tune on a few thousand labeled
        frames.
      </p>

      <div className="usecases-grid">
        {USE_CASES.map((u, i) => (
          <div className="usecase" key={u.k}>
            <div className="usecase-num mono">
              {String(i + 1).padStart(2, "0")}
            </div>
            <div className="t-heading-sm">{u.k}</div>
            <p
              className="t-body-sm"
              style={{ color: "var(--text-secondary)", marginTop: 4 }}
            >
              {u.d}
            </p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ---- Proof ----
const LogoStripe = ({ label }: { label: string }) => (
  <div className="logo-stripe placeholder-stripe">
    <span className="mono">[{label}]</span>
  </div>
);

export const ProofSection = () => (
  <section className="section" id="proof">
    <div className="container">
      <div className="t-eyebrow" style={{ color: "var(--text-tertiary)" }}>
        Proof
      </div>
      <h2 className="t-heading-xl" style={{ marginTop: 10 }}>
        Open download counts. Real customers. Measurable results.
      </h2>

      <div className="proof-stats">
        <div className="proof-stat">
          <div className="proof-num tabular">5,120,000</div>
          <div className="proof-k">Monthly downloads</div>
        </div>
        <div className="proof-stat">
          <div className="proof-num tabular">9,926</div>
          <div className="proof-k">GitHub stars</div>
        </div>
        <div className="proof-stat">
          <div className="proof-num tabular">10,000+</div>
          <div className="proof-k">Developers using it</div>
        </div>
        <div className="proof-stat">
          <div className="proof-num tabular">142</div>
          <div className="proof-k">Production deployments</div>
        </div>
      </div>

      <div className="logos-row">
        {Array.from({ length: 6 }).map((_, i) => (
          <LogoStripe key={i} label={`customer-logo-${i + 1}`} />
        ))}
      </div>

      <div className="testimonials-grid">
        <blockquote className="testimonial card">
          <p className="t-body-lg">
            &quot;We replaced a GPT-4o pipeline with Moondream on Jetson at the
            edge. Per-frame cost went from four cents to a tenth of a cent, and
            latency dropped from 1.8 seconds to 190 milliseconds.&quot;
          </p>
          <div className="testimonial-by">
            <div className="avatar-ph" />
            <div>
              <div className="t-caption">Lead ML Engineer</div>
              <div
                className="t-caption"
                style={{ color: "var(--text-tertiary)" }}
              >
                [customer-name — case study]
              </div>
            </div>
          </div>
        </blockquote>
        <blockquote className="testimonial card">
          <p className="t-body-lg">
            &quot;Lens got us from 78% mAP on our defect classes to 96% in one
            afternoon. We own the weights, and they run on the same box the
            cameras are plugged into.&quot;
          </p>
          <div className="testimonial-by">
            <div className="avatar-ph" />
            <div>
              <div className="t-caption">CTO</div>
              <div
                className="t-caption"
                style={{ color: "var(--text-tertiary)" }}
              >
                [customer-name — case study]
              </div>
            </div>
          </div>
        </blockquote>
      </div>
    </div>
  </section>
);

// ---- Mission CTA ----
// Mission-control closing band: bold slogan on a dark console panel.
export const MissionCta = () => {
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  return (
    <section className="section mission-cta" id="cta">
      <div className="container mission-cta-inner">
        <h2 className="mission-cta-headline">
          Every machine will <span>see.</span>
        </h2>
        <p className="mission-cta-sub">Build with Moondream 3.1 today.</p>
        <div className="mission-cta-actions">
          <Link href="/playground" className="btn btn-primary">
            Try the playground <IconArrowUpRight size={15} />
          </Link>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setContactDialogOpen(true)}
          >
            Talk to us
          </button>
        </div>
      </div>
      <ContactFormDialog
        open={contactDialogOpen}
        onOpenChange={setContactDialogOpen}
      />
    </section>
  );
};

// ---- Footer ----
export const Footer = () => (
  <footer className="footer">
    <div className="container">
      <div className="footer-top">
        <div className="footer-brand">
          <MoondreamWordmark />
          <p
            className="t-body-sm"
            style={{
              color: "var(--text-secondary)",
              marginTop: 12,
              maxWidth: 320,
            }}
          >
            A production vision language model. Built by M87 Labs.
          </p>
        </div>
        <div className="footer-cols">
          <div>
            <div className="footer-k">Product</div>
            <Link href="/p/models">Open Models</Link>
            <Link href="/p/lens">Lens</Link>
            <Link href="/p/photon">Photon</Link>
            <Link href="/p/cloud">Moondream Cloud</Link>
            <Link href="/pricing">Plans &amp; support</Link>
          </div>
          <div>
            <div className="footer-k">Developers</div>
            <a
              href="https://docs.moondream.ai"
              target="_blank"
              rel="noreferrer"
            >
              Docs
            </a>
            <Link href="/playground">Playground</Link>
            <a
              href="https://github.com/m87-labs/moondream"
              target="_blank"
              rel="noreferrer"
            >
              GitHub
            </a>
            <a
              href="https://huggingface.co/moondream"
              target="_blank"
              rel="noreferrer"
            >
              Hugging Face
            </a>
          </div>
          <div>
            <div className="footer-k">Company</div>
            <Link href="/about/us">About</Link>
            <Link href="/blog">Blog</Link>
            <Link href="/about/careers">Careers</Link>
            <a href="mailto:contact@moondream.ai">Press</a>
            <a href="mailto:contact@moondream.ai">Contact</a>
          </div>
          <div>
            <div className="footer-k">Legal</div>
            <Link href="/privacy">Privacy</Link>
            <Link href="/terms">Terms</Link>
            <Link href="/licenses/model/1.0">Model license</Link>
          </div>
        </div>
      </div>
      <div className="footer-bottom">
        <span
          className="t-caption"
          style={{ color: "var(--text-tertiary)" }}
        >
          © 2026 M87 Labs, Inc.
        </span>
        <span
          className="t-caption"
          style={{
            color: "var(--text-tertiary)",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          Proudly built in the USA
          <svg
            width="18"
            height="12"
            viewBox="0 0 19 10"
            aria-hidden="true"
            style={{ display: "block", borderRadius: 1 }}
          >
            <rect width="19" height="10" fill="#fff" />
            {[0, 2, 4, 6, 8, 10, 12].map((i) => (
              <rect
                key={i}
                y={(i * 10) / 13}
                x="0"
                width="19"
                height={10 / 13}
                fill="#B22234"
              />
            ))}
            <rect width="7.6" height={(7 * 10) / 13} fill="#3C3B6E" />
            {[0, 1, 2, 3].flatMap((row) =>
              [0, 1, 2, 3, 4].map((col) => (
                <circle
                  key={`${row}-${col}`}
                  cx={0.8 + col * 1.4 + (row % 2) * 0.7}
                  cy={0.55 + row * 1.0}
                  r="0.22"
                  fill="#fff"
                />
              ))
            )}
          </svg>
        </span>
      </div>
    </div>
  </footer>
);
